import axios from "axios";
import {
	LessonFilters,
	LessonMonths,
	LessonsAddedResponse,
	LessonsStatusPeriod,
	LessonsStatusResponse,
	MapProject,
	MyProjectsCount,
	MyTasksSummary,
	ProjectFilter,
	ReportPeriod,
	ReportsApprovedResponse,
	ReportStatusResponse,
	WidgetTarget,
} from "../../types/dashboard.types";
import { AuthHeader, url } from "../../utils/_exports";

const apiURL = "dashboard";

/**
 * Errors propagate so the widgets can show their error state. An API path
 * that is not mounted answers with the app's HTML page and a 200, so anything
 * but a JSON object counts as an error too.
 */
const getDashboardData = async <T>(
	tenantKey: string,
	endpoint: string,
	params: Record<string, unknown> = {}
): Promise<T> => {
	const { data } = await axios.get<unknown>(
		`${url}/${tenantKey}/${apiURL}/${endpoint}`,
		{ ...AuthHeader, params }
	);
	if (!data || typeof data !== "object" || Array.isArray(data)) {
		throw new TypeError(`Unexpected response from dashboard/${endpoint}`);
	}
	return data as T;
};

export const getReportStatus = (
	{ tenantKey, scope }: WidgetTarget,
	period: ReportPeriod,
	filters: ProjectFilter = {}
) =>
	getDashboardData<ReportStatusResponse>(tenantKey, "report-status", {
		scope,
		period,
		...filters,
	});

export const getReportsApproved = (
	{ tenantKey, scope }: WidgetTarget,
	year: number,
	filters: ProjectFilter = {}
) =>
	getDashboardData<ReportsApprovedResponse>(tenantKey, "reports-approved", {
		scope,
		year,
		...filters,
	});

export const getLessonsAdded = (
	{ tenantKey, scope }: WidgetTarget,
	months: LessonMonths,
	filters: LessonFilters = {}
) =>
	getDashboardData<LessonsAddedResponse>(tenantKey, "lessons-added", {
		scope,
		months,
		...filters,
	});

export const getLessonsStatus = (
	{ tenantKey, scope }: WidgetTarget,
	period: LessonsStatusPeriod,
	filters: Omit<LessonFilters, "lessonType"> = {}
) =>
	getDashboardData<LessonsStatusResponse>(tenantKey, "lessons-status", {
		scope,
		period,
		...filters,
	});

/** Also read by the header badge, outside the page's error boundary. */
export const getMyTasksSummary = async ({ tenantKey, scope }: WidgetTarget) => {
	const summary = await getDashboardData<Partial<MyTasksSummary>>(
		tenantKey,
		"my-tasks",
		{ scope }
	);
	if (typeof summary.totals?.pending !== "number" || !summary.byType) {
		throw new TypeError("Unexpected response from dashboard/my-tasks");
	}
	return summary as MyTasksSummary;
};

export const getMapProjects = async (tenantKey: string) => {
	const { projects } = await getDashboardData<{ projects?: MapProject[] }>(
		tenantKey,
		"map-projects"
	);
	if (!Array.isArray(projects)) {
		throw new TypeError("Unexpected response from dashboard/map-projects");
	}
	return projects;
};

/** `tenantKey` is the URL tenant; `tenantKeys` are the tenants counted. */
export const getMyProjectsCount = (tenantKey: string, tenantKeys: string[]) =>
	getDashboardData<MyProjectsCount>(tenantKey, "my-projects", {
		tenants: tenantKeys.join(","),
	});

/** The caller's open reports across tenants; `tenantKey` is only the URL tenant. */
export const getMyOpenReportsCount = async (tenantKey: string) => {
	const { count } = await getDashboardData<{ count?: number }>(
		tenantKey,
		"my-reports"
	);
	if (typeof count !== "number") {
		throw new TypeError("Unexpected response from dashboard/my-reports");
	}
	return count;
};
